import React, { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import moment from "moment";
import styled from "styled-components"

// COMPONENTS
import Button from "Components/Button"
import CalendarComponent from "Components/CalendarComponent"

// UTILS
import { getTranslation } from "Utils/getTranslation"

import {
  getFormatFromType,
  getStartAndEndOfFromType,
  getDateInNanoSec,
} from "Components/chartsLibrary/CalendarFilter/calendarUtils.js"
import {
  SaisieBloc,
  SaisieBouton,
  SaisieChamp,
} from "Components/chartsLibrary/CalendarFilter/index.style.js"

const Container = styled.div`
  border-radius: ${({ calendarBorderRadius, activeBorder }) =>
    activeBorder && calendarBorderRadius && calendarBorderRadius};
  border-style: ${({ calendarBorderStyle, activeBorder }) =>
    activeBorder && calendarBorderStyle && calendarBorderStyle};
  border-color: ${({ calendarBorderColor, activeBorder }) =>
    activeBorder && calendarBorderColor && calendarBorderColor};
  border-width: ${({ calendarBorderSize, activeBorder }) =>
    activeBorder && calendarBorderSize && calendarBorderSize};
`

const CalendarFilter = ({
  defaultColor,
  languageId,
  onSelectComparisonValue,
  onSelectFilterValue,
  onRemoveSelectedValue,
  onRemoveComparisonValue,
  onlyOneDate,
  type,
  placeholder,
  layout,
  currentColsNumber,
  breakpoint,
  isAWidgetFromSettings,
  activeBorder,
  calendarBorderRadius,
  calendarBorderStyle,
  calendarBorderColor,
  calendarBorderSize,
  /**
   * Autorise la SAISIE des dates, en plus de leur sélection au calendrier (demande du client CRC,
   * 2026-09-22). Défaut `false` : le champ s'insère dans le panneau ouvert, mais c'est un ajout de
   * comportement sur un filtre que tous les dashboards utilisent.
   */
  allowDateTyping = false,
}) => {
  const [displayCalendar, setDisplayCalendar] = useState(false)
  const [buttonTitle, setButtonTitle] = useState(placeholder)
  const [selectedDates, setSelectedDates] = useState([])

  useEffect(() => {
    var calendarButtonText = document.getElementById("calendarButtonText")
    if (calendarButtonText) {
      var marginTopToSet = "20px"
      if (layout.w > 2) marginTopToSet = "0px"
      calendarButtonText.style.setProperty("margin-top", marginTopToSet)
    }
  }, [layout.w])

  useEffect(() => {
    setSelectedDates([])
    setButtonTitle(placeholder)
  }, [type])

  useEffect(() => {
    setButtonTitle(placeholder)
  }, [placeholder])

  const {
    FROM,
    TO,
    VALIDATE,
    ERASE,
    SELECTION_OF_AN_ANALYSIS_PERIOD,
    SELECT_A_COMPARISON_PERIOD,
    YOU_DID_NOT_SELECT_ANALYSIS_PERIOD,
    TRIMESTER,
    SEMESTER,
  } = useMemo(() => getTranslation(languageId), [languageId])

  const getOnlyOneDateAsBoolean = onlyOneDate => {
    if (typeof onlyOneDate === "boolean") return onlyOneDate
    else if (typeof onlyOneDate === "string") return onlyOneDate === "true"
    return false
  }

  const languagesForCalendar = ["en", "fr"]
  const onlyOneDateInBool = getOnlyOneDateAsBoolean(onlyOneDate)
  const displayCalendarRightSide = parseInt(layout.x) >= parseInt(currentColsNumber) / 2
  const isMobileView = breakpoint === "sm"

  useEffect(() => {
    if (displayCalendar) {
      var mainGridContainer = document.getElementById("mainGridContainer")
      var overlay = document.createElement("div")
      overlay.setAttribute(
        "style",
        "position: absolute; height: 100%; width: 100%; top:0; left: 0;"
      )
      overlay.onclick = () => setDisplayCalendar(false)
      overlay.id = "overlayCalendar"
      if (mainGridContainer) mainGridContainer.appendChild(overlay)

      var fixedLayout = document.getElementById("resizable")
      if (fixedLayout) {
        fixedLayout.style.removeProperty("overflow")
        fixedLayout.style.removeProperty("overflow-y")
      }

      var screenshotContainer = document.getElementById("screenshotContainer")
      if (screenshotContainer) screenshotContainer.style.setProperty("overflow-y", "auto")
    } else {
      var overlay = document.getElementById("overlayCalendar")
      if (overlay) overlay.remove()
      var fixedLayout = document.getElementById("resizable")
      if (fixedLayout) fixedLayout.style.setProperty("overflow-y", "auto")
      var screenshotContainer = document.getElementById("screenshotContainer")
      if (screenshotContainer) screenshotContainer.style.removeProperty("overflow-y")
    }
  }, [displayCalendar])

  const getJSXTitle = (firstText, secondText) => {
    var dafaultStyle = { overflow: "hidden" }
    var smallStyle = { marginTop: "20px" }
    return (
      <div
        id="calendarButtonText"
        style={layout.w <= 2 ? { ...smallStyle, ...dafaultStyle } : { ...dafaultStyle }}
      >
        <div>
          {firstText}
          <br />
          {secondText}
        </div>
      </div>
    )
  }

  const getWidgetButtonTitle = (dateToDisplay, calendarType) => {
    var format = getFormatFromType(calendarType)
    var startAndEndOf = getStartAndEndOfFromType(calendarType)

    if (dateToDisplay.length > 1 && dateToDisplay[0] === null && dateToDisplay[1] === null) {
      return "Date"
    }

    if (
      dateToDisplay.length === 2 &&
      Array.isArray(dateToDisplay[0]) &&
      Array.isArray(dateToDisplay[1]) &&
      dateToDisplay[0].length === 2 &&
      dateToDisplay[1].length === 2
    ) {
      var firstText = ""
      var secondText = ""
      dateToDisplay.forEach((dateArray, index) => {
        var periodNumber = index
        var periodStart = moment(dateArray[0], format).startOf(startAndEndOf).format("DD/MM/YYYY")
        var periodEnd
        if (calendarType === "semester") {
          periodEnd = moment(dateArray[1], format)
            .add(5, "months")
            .endOf("month")
            .format("DD/MM/YYYY")
        } else {
          periodEnd = moment(dateArray[1], format).endOf(startAndEndOf).format("DD/MM/YYYY")
        }
        var textResult = `P${periodNumber + 1}: ${FROM} ${periodStart} ${TO} ${periodEnd}`
        if (index === 0) firstText = textResult
        else secondText = textResult
      })

      var jsxTitle = getJSXTitle(firstText, secondText)
      return jsxTitle
    }

    var firstDateDDMMYYYY = moment(dateToDisplay[0], format)
      .startOf(startAndEndOf)
      .format("DD/MM/YYYY")
    var lastDateDDMMYYYY = ""
    if (dateToDisplay?.length > 1) {
      if (calendarType === "semester") {
        lastDateDDMMYYYY = moment(dateToDisplay[1], format)
          .add(5, "months")
          .endOf("month")
          .format("DD/MM/YYYY")
      } else {
        lastDateDDMMYYYY = moment(dateToDisplay[1], format)
          .endOf(startAndEndOf)
          .format("DD/MM/YYYY")
      }
    }

    if (dateToDisplay?.length === 1) return firstDateDDMMYYYY
    else if (dateToDisplay?.length > 1)
      return `${FROM} ${firstDateDDMMYYYY} ${TO} ${lastDateDDMMYYYY}`
    return "Date"
  }

  const withoutCompareValidation = (dates, calendarType) => {
    let newButtonTitle = getWidgetButtonTitle(dates, calendarType)
    setButtonTitle(newButtonTitle)

    let datesInFilter = null
    var format = getFormatFromType(calendarType)
    var startAndEndOf = getStartAndEndOfFromType(calendarType)

    if (dates.length) {
      var firstDateInNanoSec
      var lastDateInNanoSec

      if (calendarType === "semester") {
        var endOfSemester = moment(dates[1], "MM/YYYY").add(5, "months").format("MM/YYYY")
        firstDateInNanoSec = getDateInNanoSec(dates[0], format, "month", true)
        lastDateInNanoSec = getDateInNanoSec(endOfSemester, format, "month", false)
      } else {
        firstDateInNanoSec = getDateInNanoSec(dates[0], format, startAndEndOf, true)
        lastDateInNanoSec = getDateInNanoSec(dates[1], format, startAndEndOf, false)
      }
      datesInFilter = [firstDateInNanoSec, lastDateInNanoSec]
    }

    const filtersToReturn = {
      value: datesInFilter,
      operation: [">=", "<="],
    }

    onRemoveComparisonValue()

    setSelectedDates(dates)
    if (
      datesInFilter === null ||
      datesInFilter.length === 0 ||
      datesInFilter[0] === null ||
      isNaN(datesInFilter[0])
    ) {
      onRemoveSelectedValue()
    } else {
      onSelectFilterValue(filtersToReturn)
    }
  }

  const datesHasAllValuesForComparison = dates => {
    return (
      Array.isArray(dates) &&
      dates.length === 2 &&
      Array.isArray(dates[0]) &&
      Array.isArray(dates[1]) &&
      dates[0].length === 2 &&
      dates[1].length === 2
    )
  }

  const onCompareValidation = (dates, calendarType) => {
    let newButtonTitle = getWidgetButtonTitle(dates, calendarType)
    setButtonTitle(newButtonTitle)

    var format = getFormatFromType(calendarType)
    var startAndEndOf = getStartAndEndOfFromType(calendarType)

    setSelectedDates(dates)

    if (datesHasAllValuesForComparison(dates)) {
      var beginOfFirstPeriod = dates[0][0]
      var endOfFirstPeriod = dates[0][1]
      var beginOfSecondPeriod = dates[1][0]
      var endOfSecondPeriod = dates[1][1]
      if (calendarType === "semester") {
        endOfFirstPeriod = moment(dates[0][1], "MM/YYYY").add(5, "months").format("MM/YYYY")
        endOfSecondPeriod = moment(dates[1][1], "MM/YYYY").add(5, "months").format("MM/YYYY")
      }

      var firstPeriod = [
        getDateInNanoSec(beginOfFirstPeriod, format, startAndEndOf, true),
        getDateInNanoSec(endOfFirstPeriod, format, startAndEndOf, true),
      ]
      var secondPeriod = [
        getDateInNanoSec(beginOfSecondPeriod, format, startAndEndOf, true),
        getDateInNanoSec(endOfSecondPeriod, format, startAndEndOf, false),
      ]

      const filtersToReturn = {
        firstValue: firstPeriod,
        secondValue: secondPeriod,
        operation: [">=", "<="],
      }

      onSelectComparisonValue(filtersToReturn)
    } else {
      onRemoveComparisonValue()
    }
  }

  const onDatesValidation = (dates, calendarType, isCompared) => {
    if (!isCompared) {
      withoutCompareValidation(dates, calendarType)
    } else {
      onCompareValidation(dates, calendarType)
    }
  }

  const getPreselectedFirstDate = dates => {
    if (dates === undefined) return ""
    if (dates?.length > 0) {
      if (Array.isArray(dates[0]) && dates[0].length > 0) return dates[0][0]
      else return dates[0]
    }
    return ""
  }

  const getPreselectedSecondDate = dates => {
    if (dates === undefined) return ""
    if (dates?.length > 0) {
      if (Array.isArray(dates[0]) && dates[0].length > 1) return dates[0][1]
      else if (dates.length > 1) return dates[1]
    }
    return ""
  }

  const getPreselectedThirdDate = dates => {
    if (dates === undefined) return ""
    if (dates?.length > 1 && Array.isArray(dates[1]) && dates[1].length > 0) {
      return dates[1][0]
    }
    return ""
  }

  const getPreselectedForthDate = dates => {
    if (dates === undefined) return ""
    if (dates?.length > 1 && Array.isArray(dates[1]) && dates[1].length > 1) {
      return dates[1][1]
    }
    return ""
  }

  /**
   * SAISIE DIRECTE — deux champs texte au-dessus du calendrier.
   *
   * ⚠️ Elle réutilise `withoutCompareValidation`, le chemin de validation du calendrier : c'est lui
   * qui convertit en nanosecondes, choisit les bornes de début/fin selon le type, met à jour le
   * titre du bouton et émet le filtre. Reproduire cette conversion ici aurait créé deux définitions
   * de la même période, qui auraient dérivé.
   *
   * ⚠️ Le format attendu est celui du TYPE de calendrier — `DD/MM/YYYY` au jour, `MM/YYYY` au mois
   * ou au trimestre, `YYYY` à l'année. Il est affiché en repère dans les champs, faute de quoi
   * personne ne peut deviner que « 10/2026 » se saisit ainsi et non « octobre 2026 ».
   *
   * ⚠️ Hors mode COMPARAISON, qui porte quatre dates et son propre flux. La demande visait la
   * sélection simple.
   */
  const formatSaisie = getFormatFromType(type)
  const [saisieDebut, setSaisieDebut] = useState("")
  const [saisieFin, setSaisieFin] = useState("")
  const dateValide = valeur => valeur !== "" && moment(valeur, formatSaisie, true).isValid()
  const saisieComplete = dateValide(saisieDebut) && dateValide(saisieFin)
  // Une période à l'envers ne dit rien au moteur : `>= fin` et `<= début` ne ramènent aucune ligne.
  const saisieOrdonnee =
    saisieComplete &&
    moment(saisieDebut, formatSaisie, true).isSameOrBefore(moment(saisieFin, formatSaisie, true))

  const appliquerSaisie = () => {
    if (!saisieOrdonnee) return
    withoutCompareValidation([saisieDebut, saisieFin], type)
    setDisplayCalendar(false)
  }

  const blocDeSaisie = allowDateTyping ? (
    <SaisieBloc>
      <SaisieChamp
        aria-label={FROM}
        placeholder={`${FROM} (${formatSaisie})`}
        value={saisieDebut}
        $couleur={defaultColor}
        $invalide={saisieDebut !== "" && !dateValide(saisieDebut)}
        onChange={e => setSaisieDebut(e.target.value)}
        onKeyDown={e => e.key === "Enter" && appliquerSaisie()}
      />
      <SaisieChamp
        aria-label={TO}
        placeholder={`${TO} (${formatSaisie})`}
        value={saisieFin}
        $couleur={defaultColor}
        $invalide={saisieFin !== "" && !dateValide(saisieFin)}
        onChange={e => setSaisieFin(e.target.value)}
        onKeyDown={e => e.key === "Enter" && appliquerSaisie()}
      />
      <SaisieBouton type="button" $couleur={defaultColor} disabled={!saisieOrdonnee} onClick={appliquerSaisie}>
        {VALIDATE}
      </SaisieBouton>
    </SaisieBloc>
  ) : null

  const shouldDisplayComparingOnMount = dates => {
    return (
      dates &&
      Array.isArray(dates) &&
      dates.length >= 2 &&
      Array.isArray(dates[0]) &&
      Array.isArray(dates[1]) &&
      dates[0].length >= 2 &&
      dates[1].length >= 2
    )
  }

  return (
    <Container
      activeBorder={activeBorder}
      calendarBorderRadius={calendarBorderRadius}
      calendarBorderStyle={calendarBorderStyle}
      calendarBorderColor={calendarBorderColor}
      calendarBorderSize={calendarBorderSize}
    >
      <Button
        text={buttonTitle}
        icon={<i className="fas fa-calendar" />}
        backgroundColor={defaultColor}
        style={{ width: "100%", justifyContent: "center", padding: "0 5px" }}
        onClick={() => setDisplayCalendar(!displayCalendar)}
        buttonRole={"calendarMainButton"}
      />
      {displayCalendar && (
        <CalendarComponent
          headerSlot={blocDeSaisie}
          closeCalendar={() => setDisplayCalendar(false)}
          onDatesValidation={onDatesValidation}
          mainColor={defaultColor}
          validateBtnLabel={VALIDATE}
          deleteBtnLabel={ERASE}
          title={SELECTION_OF_AN_ANALYSIS_PERIOD}
          selectComparisonText={SELECT_A_COMPARISON_PERIOD}
          isMobileView={isMobileView}
          onlyOneDate={onlyOneDateInBool}
          position={isAWidgetFromSettings ? "relative" : "absolute"}
          displayRightSide={displayCalendarRightSide}
          returnedDateFormat="YYYY-MM-DDTHH:mm:ss[0000000]"
          type={type}
          language={languagesForCalendar[languageId]}
          zIndex="10"
          preselectedFirstDate={getPreselectedFirstDate(selectedDates)}
          preselectedSecondDate={getPreselectedSecondDate(selectedDates)}
          preselectedThirdDate={getPreselectedThirdDate(selectedDates)}
          preselectedFourthDate={getPreselectedForthDate(selectedDates)}
          displayComparingInitial={shouldDisplayComparingOnMount(selectedDates)}
          showComparingToggle={true}
          displayMonthFirstLetters={true}
          partialTrad={{
            YOU_DID_NOT_SELECT_ANALYSIS_PERIOD,
            TRIMESTER,
            SEMESTER,
          }}
        />
      )}
    </Container>
  )
}

CalendarFilter.propTypes = {
  placeholder: PropTypes.string,
  defaultColor: PropTypes.string,
  onSelectFilterValue: PropTypes.func,
  onRemoveSelectedValue: PropTypes.func,
  onlyOneDate: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  layout: PropTypes.object,
  currentColsNumber: PropTypes.number,
  breakpoint: PropTypes.string,
  type: PropTypes.oneOf(["year", "month", "day", "trimester", "semester"]),
}

CalendarFilter.defaultProps = {
  placeholder: "Date",
  defaultColor: "#01014B",
  onSelectFilterValue: () => {},
  onRemoveSelectedValue: () => {},
  onlyOneDate: false,
  layout: {},
  currentColsNumber: 1,
  breakpoint: "lg",
  type: "day",
}

export default CalendarFilter
